import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { getBaseUrl } from "../lib/base-url";
import { getApi } from "../lib/get-api";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";


interface Invite {
  id: string;
  email: string;
  role: string;
  accepted: boolean;
  token?: string;
  emailSent?: boolean;
}

/** The link the colleague opens. Same origin, hash route. */
export const inviteUrl = (token: string) =>
  `${window.location.origin}/#/invite/${token}`;

export const inviteNotice = (email: string, emailSent: boolean) =>
  emailSent
    ? `Invite sent to ${email}.`
    : `Invite link ready for ${email}. Send it to them yourself.`;

/**
 * Invite a teammate onto the project.
 *
 * The link is the invite. The API reports `emailSent`; only when it is true do
 * we say an email went out, otherwise the copy-link row leads. A pending invite
 * grants nothing: the API leaves `userId` null until the invitee signs in and
 * accepts, and every ACL path matches on `userId`.
 */
export const InviteTeammate: React.FC = () => {
  const { user, token } = useAuth();
  const { datasources, isInitialLoad } = useDatasources();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [lastLink, setLastLink] = useState("");
  const [lastEmail, setLastEmail] = useState("");
  const [lastEmailSent, setLastEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mailConfigured, setMailConfigured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getBaseUrl()}/auth/providers`)
      .then((r): Promise<{ mailConfigured?: boolean }> => (r.ok ? r.json() : Promise.resolve({})))
      .then((p) => {
        if (!cancelled) setMailConfigured(Boolean(p?.mailConfigured));
      })
      .catch(() => {
        if (!cancelled) setMailConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A project only exists once the founder has connected a datasource, which
  // is also when inviting starts to mean anything.
  const projectId = datasources.find((ds) => (ds as any).projectId)?.projectId;

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const api = getApi({ token });
      const res = await (api as any).request({
        path: `/projects/${projectId}/invites`,
        method: "GET",
        format: "json",
      });
      setInvites(res.data ?? []);
    } catch {
      // A listing failure must not blank the form the founder is typing into.
    }
  }, [projectId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    const trimmed = email.trim();
    if (!trimmed || !projectId) return;
    setSending(true);
    try {
      const api = getApi({ token });
      const res = await (api as any).request({
        path: `/projects/${projectId}/invites`,
        method: "POST",
        // MOSA-367 slice 1 moved the server default to Viewer. This box has
        // no role control, so it keeps asking for the access it has always
        // granted; the Share page is where a Viewer is invited.
        body: { email: trimmed, role: "EDITOR" },
        type: "application/json",
        format: "json",
      });
      const link = res.data?.token ? inviteUrl(res.data.token) : "";
      const sent = res.data?.emailSent === true;
      setLastLink(link);
      setLastEmail(trimmed);
      setLastEmailSent(sent);
      setCopied(false);
      setEmail("");
      toast.success(inviteNotice(trimmed, sent));
      load();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Could not create that invite",
      );
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the invite link");
    }
  };

  const revoke = async (id: string) => {
    if (!projectId) return;
    try {
      const api = getApi({ token });
      await (api as any).request({
        path: `/projects/${projectId}/invites/${id}`,
        method: "DELETE",
      });
      toast.success("Invite revoked");
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not revoke");
    }
  };

  if (!user) return null;

  return (
    <section className="bg-od-surface border border-od-border rounded-lg p-4 space-y-3">
      <h2 className="text-sm font-semibold text-od-text">Invite teammate</h2>
      <p className="text-xs text-od-muted">
        They get the tables, not the connection string.
      </p>

      {!projectId ? (
        isInitialLoad ? null : (
          <p className="text-sm text-od-muted">
            Connect a data source first, then you can invite someone to it.
          </p>
        )
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              aria-label="Teammate email"
              disabled={sending}
            />
            <Button
              onClick={send}
              disabled={sending || !email.trim()}
              variant="primary" className="text-sm"
            >
              {sending
                ? "Creating"
                : mailConfigured
                ? "Send invite"
                : "Create invite link"}
            </Button>
          </div>

          {lastEmail && (
            <p className="text-sm text-od-text" data-testid="invite-notice">
              {inviteNotice(lastEmail, lastEmailSent)}
            </p>
          )}

          {lastLink && (
            // GOAL is copy-link with no SMTP configured, so the link is the
            // real invite mechanism, not a fallback. It used to sit behind a
            // collapsed <details> ("Copy link instead"), which read as a
            // secondary path and cost the founder an extra click to reach
            // the only thing that actually invites someone. Show it open,
            // same as the "Invite sent" confirmation above it.
            <div
              className={
                lastEmailSent
                  ? "text-xs text-od-muted space-y-1"
                  : "space-y-1.5 rounded-lg border border-od-primary bg-od-bg p-2.5 text-xs text-od-muted"
              }
            >
              <p className={lastEmailSent ? "text-od-text" : "text-sm font-medium text-od-text"}>
                Invite link
              </p>
              <div className="flex items-center gap-1.5">
                <span
                  className="bg-od-bg border border-od-border text-od-text px-2 py-1 rounded text-xs flex-1 min-w-0 truncate font-mono"
                  title={lastLink}
                >
                  {lastLink}
                </span>
                <Button
                  onClick={copyLink}
                  variant={lastEmailSent ? "secondary" : "primary"}
                  size="sm"
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          )}

          {invites.length > 0 && (
            <ul className="space-y-1">
              {invites.map((invite) => (
                <li
                  key={invite.id}
                  className="flex items-center gap-2 text-sm text-od-text"
                >
                  <span className="flex-1 min-w-0 truncate">
                    {invite.email}
                  </span>
                  <span className="text-xs text-od-muted uppercase tracking-wide">
                    {invite.accepted ? "Joined" : "Pending"}
                  </span>
                  {!invite.accepted && (
                    <Button
                      onClick={() => revoke(invite.id)}
                      variant="secondary" size="sm" className="text-od-muted hover:border-od-danger hover:bg-transparent hover:text-od-danger"
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default InviteTeammate;
